import Shield from "lucide-react/dist/esm/icons/shield";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import User from "lucide-react/dist/esm/icons/user";
import UserCheck from "lucide-react/dist/esm/icons/user-check";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type TrustLevel = "new" | "active" | "trusted" | "verified";

interface BuyerTrustBadgeProps {
  level: TrustLevel;
  className?: string;
  showLabel?: boolean;
}

const trustConfig: Record<
  TrustLevel,
  {
    label: string;
    icon: React.ElementType;
    className: string;
    description: string;
  }
> = {
  new: {
    label: "New Buyer",
    icon: User,
    className: "bg-muted text-muted-foreground border-muted-foreground/20",
    description: "Recently joined the marketplace",
  },
  active: {
    label: "Active Buyer",
    icon: UserCheck,
    // Use the design-token 'info' color instead of raw blue-*
    className: "bg-info/10 text-info border-info/20",
    description: "Regular marketplace participant",
  },
  trusted: {
    label: "Trusted Buyer",
    icon: ShieldCheck,
    // Use the design-token 'success' color instead of raw green-*
    className: "bg-success/10 text-success border-success/20",
    description: "Established track record",
  },
  verified: {
    label: "Verified Buyer",
    icon: Shield,
    // Use the design-token 'warning' color instead of raw yellow-*
    className: "bg-warning/10 text-warning border-warning/20",
    description: "Verified account with excellent history",
  },
};

export function calculateTrustLevel(stats: {
  purchaseCount: number;
  reviewCount: number;
  accountAgeMonths: number;
  disputeCount: number;
  commissionCount?: number;
}): TrustLevel {
  const { purchaseCount, reviewCount, accountAgeMonths, disputeCount, commissionCount = 0 } = stats;
  const totalActivity = purchaseCount + commissionCount;

  // Any disputes significantly impact trust
  if (disputeCount > 0 && totalActivity < 20) {
    return "new";
  }

  // Verified: 20+ total activity, clean record, established account
  if (totalActivity >= 20 && disputeCount === 0 && accountAgeMonths >= 3) {
    return "verified";
  }

  // Trusted: 10+ total activity, no disputes
  if (totalActivity >= 10 && disputeCount === 0) {
    return "trusted";
  }

  // Active: 3+ purchases/commissions or reviews
  if (totalActivity >= 3 || reviewCount >= 3) {
    return "active";
  }

  return "new";
}

export function BuyerTrustBadge({
  level,
  className,
  showLabel = true,
}: BuyerTrustBadgeProps) {
  const config = trustConfig[level];
  const Icon = config.icon;

  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1.5 font-medium border",
        config.className,
        className
      )}
      title={config.description}
    >
      <Icon className="h-3.5 w-3.5" />
      {showLabel && <span>{config.label}</span>}
    </Badge>
  );
}

export function getTrustLevelDescription(level: TrustLevel): string {
  return trustConfig[level].description;
}
